import { DesignationEnum } from '../../common/models/common.model';
import {
  ReportingLineErrors,
  SalesTeamFocus,
  SalesTeamIndexEntry,
  SalesTeamLevel,
  SalesTeamNode,
  SalesTeamRow,
  SalesTeamSummary,
} from './sales-team-management.model';

const LEVEL_BY_DESIGNATION: Record<string, SalesTeamLevel> = {
  [DesignationEnum.REGIONAL_SALES_MANAGER]: SalesTeamLevel.RSM,
  [DesignationEnum.AREA_SALES_MANAGER]: SalesTeamLevel.ASM,
  [DesignationEnum.SALES_OFFICER]: SalesTeamLevel.SO,
};

/** Who each level should report to, for the tag shown while that manager is missing. */
const MISSING_MANAGER_LABEL: Partial<Record<SalesTeamLevel, string>> = {
  [SalesTeamLevel.SO]: 'No Area Sales Manager',
  [SalesTeamLevel.ASM]: 'No Regional Sales Manager',
};

export function missingManagerLabel(designation: string): string {
  const level = salesTeamLevel(designation);

  return (level && MISSING_MANAGER_LABEL[level]) || 'No manager';
}

/** Badge colour and short label per level. Anything else falls back to a neutral badge. */
export const SALES_TEAM_LEVEL_BADGE: Record<SalesTeamLevel, { shortLabel: string; badgeClass: string }> = {
  [SalesTeamLevel.RSM]: { shortLabel: 'RSM', badgeClass: 'badge-soft-primary' },
  [SalesTeamLevel.ASM]: { shortLabel: 'ASM', badgeClass: 'badge-soft-teal' },
  [SalesTeamLevel.SO]: { shortLabel: 'SO', badgeClass: 'badge-soft-purple' },
};

export function salesTeamLevel(designation: string): SalesTeamLevel | null {
  return LEVEL_BY_DESIGNATION[designation] ?? null;
}

export function isRegionalSalesManager(node: SalesTeamNode): boolean {
  return salesTeamLevel(node.designation) === SalesTeamLevel.RSM;
}

export const EMPTY_SALES_TEAM_SUMMARY: SalesTeamSummary = {
  totalCount: 0,
  rsmCount: 0,
  asmCount: 0,
  soCount: 0,
  unassignedCount: 0,
  unassignedAsmCount: 0,
  unassignedSoCount: 0,
};

/** Counts per level across the whole tree; unassigned is every top-level person who is not an RSM. */
export function summarizeSalesTeam(topLevel: SalesTeamNode[]): SalesTeamSummary {
  const summary: SalesTeamSummary = { ...EMPTY_SALES_TEAM_SUMMARY };

  const visit = (node: SalesTeamNode): void => {
    summary.totalCount++;

    switch (salesTeamLevel(node.designation)) {
      case SalesTeamLevel.RSM:
        summary.rsmCount++;
        break;
      case SalesTeamLevel.ASM:
        summary.asmCount++;
        break;
      case SalesTeamLevel.SO:
        summary.soCount++;
        break;
    }
    node.reports.forEach(visit);
  };

  topLevel.forEach(visit);
  for (const node of topLevel) {
    const level = salesTeamLevel(node.designation);

    if (level !== SalesTeamLevel.RSM) {
      summary.unassignedCount++;
    }
    if (level === SalesTeamLevel.ASM) {
      summary.unassignedAsmCount++;
    }
    if (level === SalesTeamLevel.SO) {
      summary.unassignedSoCount++;
    }
  }

  return summary;
}

/** Ids of every node that has reports, for "Expand all". */
export function expandableIds(nodes: SalesTeamNode[]): Set<string> {
  const ids = new Set<string>();

  const visit = (node: SalesTeamNode): void => {
    if (node.reports.length > 0) {
      ids.add(node.id);
      node.reports.forEach(visit);
    }
  };

  nodes.forEach(visit);

  return ids;
}

/** Focus mode input for flattening: who is in focus, and which base guide lines are on the path. */
/**
 * Something drawn after the top-level list on the same parent line (the Unassigned heading under Admin),
 * so the last top-level person is not the end of that line. `onPath` and `tone` say whether the line on
 * to it is part of the focused path, and in which colour.
 */
export interface TrailingSibling {
  onPath: boolean;
  tone: string;
}

export interface FlattenFocus {
  ids: ReadonlySet<string>;
  baseGuides: boolean[];
  /** Colour keys for the base guide lines, see SalesTeamRowPath.tones. */
  baseGuideTones: string[];
}

/** Colour key for the level a line leads into; matches the avatar and badge colours. */
export function levelTone(node: SalesTeamNode): string {
  return salesTeamLevel(node.designation) ?? 'other';
}

/**
 * Depth-first rows for the visible part of the tree: a node's reports appear only when it is expanded.
 * `baseGuides` describes the levels drawn above `nodes` (the group headings under Admin). With `focus`,
 * each row also records which of its connector segments trace the path to and through the focused branch.
 */
export function flattenSalesTeam(
  nodes: SalesTeamNode[],
  expandedIds: ReadonlySet<string>,
  baseGuides: boolean[] = [],
  focus: FlattenFocus | null = null,
  trailingSibling: TrailingSibling | null = null
): SalesTeamRow[] {
  const rows: SalesTeamRow[] = [];
  const inFocus = (node: SalesTeamNode): boolean => !!focus && focus.ids.has(node.id);

  const visit = (
    list: SalesTeamNode[],
    depth: number,
    guides: boolean[],
    pathGuides: boolean[],
    guideTones: string[]
  ): void => {
    // The parent's line runs down as far as its last child on the path, in that child's colour. At the top
    // level, a trailing sibling on the path carries the line past the whole list.
    const trailing = depth === baseGuides.length ? trailingSibling : null;
    const lastInList = list.reduce((last, node, index) => (inFocus(node) ? index : last), -1);
    const lastOnPath = trailing?.onPath ? list.length : lastInList;
    const lineTone = trailing?.onPath ? trailing.tone : lastInList >= 0 ? levelTone(list[lastInList]) : '';

    list.forEach((node, index) => {
      const isLastSibling = index === list.length - 1 && !trailing;
      const isExpanded = node.reports.length > 0 && expandedIds.has(node.id);
      const hasNoManager = depth === baseGuides.length && !isRegionalSalesManager(node);
      const path = focus
        ? {
            guides: pathGuides,
            up: index <= lastOnPath,
            down: index < lastOnPath,
            across: inFocus(node),
            stem: isExpanded && inFocus(node) && node.reports.some(inFocus),
            tones: {
              guides: guideTones,
              line: lineTone,
              across: levelTone(node),
              stem: node.reports.find(inFocus) ? levelTone(node.reports.find(inFocus)!) : '',
            },
          }
        : null;

      rows.push({
        node,
        depth,
        isLastSibling,
        guides,
        isExpanded,
        hasNoManager,
        isFocused: false,
        isDimmed: false,
        path,
      });

      if (isExpanded) {
        visit(
          node.reports,
          depth + 1,
          [...guides, !isLastSibling],
          [...pathGuides, index < lastOnPath],
          [...guideTones, lineTone]
        );
      }
    });
  };

  visit(
    nodes,
    baseGuides.length,
    baseGuides,
    focus?.baseGuides ?? baseGuides.map(() => false),
    focus?.baseGuideTones ?? baseGuides.map(() => '')
  );

  return rows;
}

/** The focused person's branch, or null when the id is not in the tree. */
export function focusBranch(nodes: SalesTeamNode[], id: string): SalesTeamFocus | null {
  const findPath = (list: SalesTeamNode[], path: SalesTeamNode[]): SalesTeamNode[] | null => {
    for (const node of list) {
      if (node.id === id) {
        return [...path, node];
      }

      const found = findPath(node.reports, [...path, node]);

      if (found) {
        return found;
      }
    }

    return null;
  };

  const path = findPath(nodes, []);

  if (!path) {
    return null;
  }

  const focused = path[path.length - 1];
  const ids = new Set(path.map(node => node.id));
  const addReports = (node: SalesTeamNode): void =>
    node.reports.forEach(report => {
      ids.add(report.id);
      addReports(report);
    });

  addReports(focused);

  return { ids, ancestorIds: path.slice(0, -1).map(node => node.id) };
}

/** Up to two initials for the avatar, e.g. "Sanjay Deshmukh" gives "SD". */
export function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

/** Every person by id, with who they report to (null for Admin). */
export function indexSalesTeam(nodes: SalesTeamNode[]): Map<string, SalesTeamIndexEntry> {
  const index = new Map<string, SalesTeamIndexEntry>();

  const visit = (list: SalesTeamNode[], managerId: string | null): void =>
    list.forEach(node => {
      index.set(node.id, { node, managerId });
      visit(node.reports, node.id);
    });

  visit(nodes, null);

  return index;
}

/** A manager is exactly one level up: ASM for an SO, RSM for an ASM. RSMs report to Admin and get none. */
const MANAGER_LEVEL: Partial<Record<SalesTeamLevel, SalesTeamLevel>> = {
  [SalesTeamLevel.SO]: SalesTeamLevel.ASM,
  [SalesTeamLevel.ASM]: SalesTeamLevel.RSM,
};

export function managerLevelFor(designation: string): SalesTeamLevel | null {
  const level = salesTeamLevel(designation);

  return level ? (MANAGER_LEVEL[level] ?? null) : null;
}

/** Who can be picked as this designation's manager: active people one level up, in tree order. */
export function managerCandidates(nodes: SalesTeamNode[], designation: string): SalesTeamNode[] {
  const managerLevel = managerLevelFor(designation);

  if (!managerLevel) {
    return [];
  }

  return [...indexSalesTeam(nodes).values()]
    .map(entry => entry.node)
    .filter(node => node.is_active && salesTeamLevel(node.designation) === managerLevel);
}

/** Shown instead of the backend's raw "Invalid pk" text, which is not user-facing. */
export const MANAGER_UNAVAILABLE_MESSAGE = 'That manager is no longer available.';
const INACTIVE_MANAGER_MESSAGE = "This manager's login is inactive.";

const asMessages = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * Sorts a 400 body from the reporting-lines endpoints by where each message belongs. Messages are kept as the
 * backend wrote them, except the raw "Invalid pk" one. `employees` comes in three shapes: a list of message
 * lists aligned with `sentEmployeeIds`, a flat list about the list as a whole, or an object keyed by position.
 */
export function parseReportingLineErrors(body: unknown, sentEmployeeIds: string[]): ReportingLineErrors {
  const errors: ReportingLineErrors = { manager: [], list: [], byEmployee: {}, other: [], refreshTree: false };
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};

  const addForPosition = (position: number, messages: string[]): void => {
    const employeeId = sentEmployeeIds[position];

    if (employeeId && messages.length) {
      errors.byEmployee[employeeId] = messages;
    } else {
      errors.list.push(...messages);
    }
  };

  for (const [key, value] of Object.entries(record)) {
    if (key === 'manager') {
      for (const message of asMessages(value)) {
        if (message.startsWith('Invalid pk')) {
          errors.manager.push(MANAGER_UNAVAILABLE_MESSAGE);
          errors.refreshTree = true;
        } else {
          errors.manager.push(message);
          errors.refreshTree ||= message === INACTIVE_MANAGER_MESSAGE;
        }
      }
    } else if (key === 'employees') {
      if (Array.isArray(value) && value.some(Array.isArray)) {
        value.forEach((messages, position) => addForPosition(position, asMessages(messages)));
      } else if (Array.isArray(value)) {
        errors.list.push(...asMessages(value));
      } else if (value && typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([position, messages]) =>
          addForPosition(Number(position), asMessages(messages))
        );
      }
    } else if (typeof value === 'string') {
      errors.other.push(value);
    } else {
      errors.other.push(...asMessages(value));
    }
  }

  return errors;
}
